import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { cn } from 'src/lib/utils';
import {
  PortalDsAlert,
  PortalDsButton,
  PortalDsCard,
  PortalDsField,
  PortalDsInput,
  PortalDsSectionHeader,
  PortalDsSelect,
  portalDs,
} from 'src/components/portal-design-system';
import {
  useCreateDataJobRunMutation,
  useFixEtlInflatedLoadMutation,
  useGetDataJobDefinitionsQuery,
  useGetDataJobFailuresQuery,
  useGetDataJobRunQuery,
  useGetDataJobRunsQuery,
  useRerunDataJobRunMutation,
} from 'src/redux/operations/operations.endpoints';
import { formatSchedulerMutationError } from 'src/helpers/scheduler-schedule.helpers';
import type { DataJobRunFormMode, DataJobRunSummary } from '../types/data-jobs.types';
import type { InflationFixSuggestion } from '../types/operations.types';
import { DataJobRunFormV2, type DataJobRunFormValuesV2 } from './data-job-run-form-v2';
import { DataJobRunListV2 } from './data-job-run-list-v2';
import { DataJobRunSheetV2 } from './data-job-run-sheet-v2';
import { ETL_RUNS_PAGE_SIZE, EtlRunsPagination } from './etl-runs-pagination';

export function EtlRunsSectionV2() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedRunId = searchParams.get('run');

  const [formMode, setFormMode] = useState<DataJobRunFormMode | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [rerunError, setRerunError] = useState<string | null>(null);
  const [inflationFixError, setInflationFixError] = useState<string | null>(null);
  const [inflationFixSuccess, setInflationFixSuccess] = useState<string | null>(null);

  const [jobKeyFilter, setJobKeyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [triggerFilter, setTriggerFilter] = useState('');
  const [activeDateFilter, setActiveDateFilter] = useState('');
  const [page, setPage] = useState(1);

  const listParams = useMemo(() => ({
    ...(jobKeyFilter ? { job_key: jobKeyFilter } : {}),
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(triggerFilter ? { trigger_type: triggerFilter } : {}),
    ...(activeDateFilter ? { active_date: activeDateFilter } : {}),
    page,
    page_size: ETL_RUNS_PAGE_SIZE,
  }), [activeDateFilter, jobKeyFilter, page, statusFilter, triggerFilter]);

  const {
    data: runsPage,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useGetDataJobRunsQuery(listParams, {
    pollingInterval: 30_000,
  });
  const runs = runsPage?.items;
  const { data: definitions = [] } = useGetDataJobDefinitionsQuery();
  const { data: failures } = useGetDataJobFailuresQuery(undefined, {
    pollingInterval: 120_000,
  });
  const {
    data: selectedRun,
    isLoading: isRunLoading,
    refetch: refetchRun,
  } = useGetDataJobRunQuery(
    { runId: selectedRunId ?? '' },
    { skip: !selectedRunId, pollingInterval: selectedRunId ? 15_000 : 0 },
  );

  const [createRun, { isLoading: isCreating }] = useCreateDataJobRunMutation();
  const [rerunRun, { isLoading: isRerunning }] = useRerunDataJobRunMutation();
  const [fixInflatedLoad, { isLoading: isFixingInflation }] = useFixEtlInflatedLoadMutation();

  const openRun = (run: DataJobRunSummary) => {
    const next = new URLSearchParams(searchParams);
    next.set('run', run.id);
    setSearchParams(next, { replace: true });
    setInflationFixError(null);
    setInflationFixSuccess(null);
  };

  const closeRun = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('run');
    setSearchParams(next, { replace: true });
    setRerunError(null);
    setInflationFixError(null);
    setInflationFixSuccess(null);
  };

  const handleFixInflation = async (fix: InflationFixSuggestion) => {
    setInflationFixError(null);
    setInflationFixSuccess(null);
    try {
      const result = await fixInflatedLoad({
        active_date: fix.active_date,
        platforms: [fix.platform],
      }).unwrap();
      setInflationFixSuccess(result.message);
      await refetch();
    } catch (error) {
      setInflationFixError(formatSchedulerMutationError(error));
    }
  };

  useEffect(() => {
    if (selectedRunId && selectedRun && selectedRun.id !== selectedRunId) {
      refetchRun();
    }
  }, [refetchRun, selectedRun, selectedRunId]);

  useEffect(() => {
    setPage(1);
  }, [jobKeyFilter, statusFilter, triggerFilter, activeDateFilter]);

  const handleCreateRun = async (values: DataJobRunFormValuesV2) => {
    try {
      const created = await createRun(values).unwrap();
      setSubmitError(null);
      setFormMode(null);
      const next = new URLSearchParams(searchParams);
      next.set('run', created.id);
      setSearchParams(next, { replace: true });
    } catch (error) {
      setSubmitError(formatSchedulerMutationError(error));
    }
  };

  const handleRerun = async () => {
    if (!selectedRunId) {
      return;
    }
    try {
      const rerun = await rerunRun({ runId: selectedRunId }).unwrap();
      setRerunError(null);
      const next = new URLSearchParams(searchParams);
      next.set('run', rerun.id);
      setSearchParams(next, { replace: true });
    } catch (error) {
      setRerunError(formatSchedulerMutationError(error));
    }
  };

  return (
    <>
      {failures?.has_alerts ? (
        <PortalDsAlert variant="danger" title="Failed ETL runs">
          {failures.alert_count}
          {' '}
          failed run(s) in the last
          {' '}
          {failures.hours}
          {' '}
          hours.
        </PortalDsAlert>
      ) : null}

      <PortalDsCard aria-labelledby="etl-runs-heading-v2">
        <PortalDsSectionHeader
          overline="Operations"
          title="ETL runs"
          description="Start manual runs, inspect progress, and review validation outcomes."
          titleId="etl-runs-heading-v2"
          actions={(
            <>
              <PortalDsButton
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => void refetch()}
                disabled={isFetching}
              >
                Refresh
              </PortalDsButton>
              <PortalDsButton
                type="button"
                variant="secondary"
                onClick={() => setFormMode('backfill')}
              >
                Start backfill
              </PortalDsButton>
              <PortalDsButton
                type="button"
                variant="accent"
                onClick={() => setFormMode('manual')}
              >
                <Plus className="size-3.5" />
                New run
              </PortalDsButton>
            </>
          )}
        />

        {formMode ? (
          <div className={cn(portalDs.formPanel, 'mb-6')}>
            <h3 className={cn(portalDs.headingH3, 'mb-4')}>
              {formMode === 'backfill' ? 'Start backfill' : 'Start manual run'}
            </h3>
            <DataJobRunFormV2
              definitions={definitions}
              mode={formMode}
              isSaving={isCreating}
              submitError={submitError}
              onSubmit={handleCreateRun}
              onCancel={() => {
                setFormMode(null);
                setSubmitError(null);
              }}
            />
          </div>
        ) : null}

        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-3">
            <PortalDsField label="Job" htmlFor="etl-run-filter-job-v2" filter>
              <PortalDsSelect
                id="etl-run-filter-job-v2"
                value={jobKeyFilter}
                onChange={(event) => setJobKeyFilter(event.target.value)}
              >
                <option value="">All</option>
                {definitions.map((definition) => (
                  <option key={definition.key} value={definition.key}>
                    {definition.label}
                  </option>
                ))}
              </PortalDsSelect>
            </PortalDsField>
            <PortalDsField label="Status" htmlFor="etl-run-filter-status-v2" filter>
              <PortalDsSelect
                id="etl-run-filter-status-v2"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <option value="">All</option>
                {['pending', 'queued', 'running', 'success', 'warning', 'failed', 'cancelled', 'interrupted'].map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </PortalDsSelect>
            </PortalDsField>
            <PortalDsField label="Trigger" htmlFor="etl-run-filter-trigger-v2" filter>
              <PortalDsSelect
                id="etl-run-filter-trigger-v2"
                value={triggerFilter}
                onChange={(event) => setTriggerFilter(event.target.value)}
              >
                <option value="">All</option>
                {['scheduled', 'manual', 'backfill', 'rerun'].map((trigger) => (
                  <option key={trigger} value={trigger}>{trigger}</option>
                ))}
              </PortalDsSelect>
            </PortalDsField>
            <PortalDsField label="Active date" htmlFor="etl-run-filter-date-v2" filter>
              <PortalDsInput
                id="etl-run-filter-date-v2"
                type="date"
                value={activeDateFilter}
                onChange={(event) => setActiveDateFilter(event.target.value)}
              />
            </PortalDsField>
          </div>

          <DataJobRunListV2
            runs={runs}
            isLoading={isLoading}
            isError={isError}
            onSelect={openRun}
          />

          <EtlRunsPagination
            page={runsPage?.page ?? page}
            totalPages={runsPage?.total_pages ?? 0}
            totalObjects={runsPage?.total_objects ?? 0}
            pageSize={runsPage?.page_size ?? ETL_RUNS_PAGE_SIZE}
            onPageChange={setPage}
            disabled={isFetching}
          />
        </div>
      </PortalDsCard>

      <DataJobRunSheetV2
        open={Boolean(selectedRunId)}
        run={selectedRun}
        isLoading={isRunLoading}
        isRerunning={isRerunning}
        isFixingInflation={isFixingInflation}
        rerunError={rerunError}
        inflationFixError={inflationFixError}
        inflationFixSuccess={inflationFixSuccess}
        onOpenChange={(open) => {
          if (!open) {
            closeRun();
          }
        }}
        onRerun={handleRerun}
        onFixInflation={(fix) => {
          void handleFixInflation(fix);
        }}
      />
    </>
  );
}
